import { Field, useFormikContext } from 'formik';

import { InputUI, FormErrorUI, FormLabelUI, AutoCompleteSelectUI, DatePickerUI } from '../Interface';
import { useEffect, useState } from 'react';
import DynamicPropertyField from '../DynamicProperty/DynamicPropertyField';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import budgetService from '../../service/budget.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const BudgetFields = ({ viewField, autoCompleteOptions, selectedValues, autocompleteData, dynProps }) => {
  const { params } = useParam();
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const [projectDynValues, setProjectDynValues] = useState([]);

  useEffect(() => {
    if (values.projectId) {
      const findedProject = autocompleteData?.projects.find((item) => item.projectId === values.projectId);
      setFieldValue('companyName', findedProject?.company?.name || '');
      setFieldValue('projectOrderNumber', findedProject.orderNumber || '');
      setProjectDynValues(findedProject);
    } else {
      setFieldValue('companyName', '');
      setFieldValue('projectOrderNumber', '');
      setProjectDynValues([]);
    }
  }, [values.projectId]);

  const getRowByProjectId = async (projectId) => {
    const foundBudget = await budgetService.getRowByProjectId(projectId);

    if (foundBudget.id) {
      addNotification({
        content: 'Költségvetés már létezik a munkaszámhoz!',
        type: NotificationType.ERROR,
      });
      return true;
    }
    return false;
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4">
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="projectId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectId} />}
          onChange={async (_e, newVal) => {
            const projectId = newVal?.value ?? null;

            if (!projectId) {
              await setFieldValue('projectId', null);
              await validateField('projectId');
              return;
            }

            const hasBudget = await getRowByProjectId(projectId);

            if (!hasBudget) {
              await setFieldValue('projectId', projectId);
              await validateField('projectId');
            }
          }}
          InputLabelProps={{ shrink: values.projectId !== '' }}
          selectedValue={values.projectId}
          selectedLabelValue={selectedValues?.project?.projectName}
          dataset={autoCompleteOptions?.projects}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="date"
          component={DatePickerUI}
          value={values.date}
          fullWidth
          label={<FormLabelUI text="Dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.date} />}
          onChange={(date) => {
            setFieldValue('date', date).then(() => {
              validateField('date');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.date) }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="currencyCode"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Pénznem" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.currencyCode} />}
          onChange={(_e, newVal) => {
            setFieldValue('currencyCode', newVal?.value ?? null).then(() => {
              validateField('currencyCode');
            });
          }}
          InputLabelProps={{ shrink: values.currencyCode !== '' }}
          selectedValue={values.currencyCode}
          selectedLabelValue={selectedValues?.currencyCode}
          dataset={autoCompleteOptions?.currency}
          disabled
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="text"
          name="companyName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cég" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.companyName} />}
          disabled
          InputLabelProps={{ shrink: values.companyName && values.companyName !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="projectOrderNumber"
          component={InputUI}
          fullWidth
          label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectOrderNumber} />}
          disabled={true}
          InputLabelProps={{ shrink: values.projectOrderNumber && values.projectOrderNumber !== '' }}
          value={values.projectOrderNumber}
        />
      </div>
      {dynProps.length > 0 &&
        dynProps?.map((dynProp) => (
          <div className="col-span-4 md:col-span-1">
            <DynamicPropertyField
              fieldData={dynProp}
              name={`dynamicFields.din_${dynProp.property.id}`}
              selectedValues={projectDynValues}
              showLabel={true}
              disabled
              InputLabelProps={{ shrink: values.projectId }}
            />
          </div>
        ))}
    </div>
  );
};

export default BudgetFields;
